import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
    InternalConversation,
    InternalMessage,
} from '@/lib/api/internal-chat/internal-chat-api'

export interface TypingUser {
    conversationId: number
    userId: number
    userName: string
}

interface InternalChatState {
    conversations: InternalConversation[]
    currentConversation: InternalConversation | null
    messages: InternalMessage[]
    unreadCount: number
    loading: boolean
    typingUsers: TypingUser[]
    replyingTo: InternalMessage | null
}

const initialState: InternalChatState = {
    conversations: [],
    currentConversation: null,
    messages: [],
    unreadCount: 0,
    loading: false,
    typingUsers: [],
    replyingTo: null,
}

const internalChatSlice = createSlice({
    name: 'internalChat',
    initialState,
    reducers: {
        setConversations: (
            state,
            action: PayloadAction<InternalConversation[]>
        ) => {
            state.conversations = action.payload
        },
        addConversation: (
            state,
            action: PayloadAction<InternalConversation>
        ) => {
            const exists = state.conversations.find(
                c => c.id === action.payload.id
            )
            if (!exists) {
                state.conversations.unshift(action.payload)
            }
        },
        setCurrentConversation: (
            state,
            action: PayloadAction<InternalConversation | null>
        ) => {
            state.currentConversation = action.payload
        },
        setMessages: (state, action: PayloadAction<InternalMessage[]>) => {
            state.messages = action.payload
        },
        addMessage: (state, action: PayloadAction<InternalMessage>) => {
            const exists = state.messages.some(m => m.id === action.payload.id)
            if (!exists) {
                state.messages.push(action.payload)
            }
        },
        updateMessageStatus: (
            state,
            action: PayloadAction<{
                messageId: number
                status: string
            }>
        ) => {
            const message = state.messages.find(
                m => m.id === action.payload.messageId
            )
            if (message) {
                message.status = action.payload.status as any
            }
        },
        setUnreadCount: (state, action: PayloadAction<number>) => {
            state.unreadCount = action.payload
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload
        },
        addTypingUser: (state, action: PayloadAction<TypingUser>) => {
            const exists = state.typingUsers.some(
                t =>
                    t.conversationId === action.payload.conversationId &&
                    t.userId === action.payload.userId
            )
            if (!exists) {
                state.typingUsers.push(action.payload)
            }
        },
        removeTypingUser: (
            state,
            action: PayloadAction<{ conversationId: number; userId: number }>
        ) => {
            state.typingUsers = state.typingUsers.filter(
                t =>
                    !(
                        t.conversationId === action.payload.conversationId &&
                        t.userId === action.payload.userId
                    )
            )
        },
        setReplyingTo: (
            state,
            action: PayloadAction<InternalMessage | null>
        ) => {
            state.replyingTo = action.payload
        },
        updateConversationLastMessage: (
            state,
            action: PayloadAction<{
                id: number
                preview: string
                timestamp: string
            }>
        ) => {
            const conv = state.conversations.find(
                c => c.id === action.payload.id
            )
            if (conv) {
                conv.lastMessagePreview = action.payload.preview
                conv.lastMessageAt = action.payload.timestamp
            }
            // Reorder conversations by lastMessageAt
            state.conversations.sort((a, b) => {
                if (!a.lastMessageAt) return 1
                if (!b.lastMessageAt) return -1
                return (
                    new Date(b.lastMessageAt).getTime() -
                    new Date(a.lastMessageAt).getTime()
                )
            })
        },
        incrementConversationUnread: (state, action: PayloadAction<number>) => {
            const conv = state.conversations.find(c => c.id === action.payload)
            if (conv) {
                conv.unreadCount = (conv.unreadCount || 0) + 1
            }
            state.unreadCount += 1
        },
        resetConversationUnread: (state, action: PayloadAction<number>) => {
            const conv = state.conversations.find(c => c.id === action.payload)
            if (conv) {
                state.unreadCount = Math.max(
                    0,
                    state.unreadCount - (conv.unreadCount || 0)
                )
                conv.unreadCount = 0
            }
        },
        clearAll: state => {
            state.conversations = []
            state.currentConversation = null
            state.messages = []
            state.unreadCount = 0
            state.typingUsers = []
            state.replyingTo = null
        },
    },
})

export const {
    setConversations,
    addConversation,
    setCurrentConversation,
    setMessages,
    addMessage,
    updateMessageStatus,
    setUnreadCount,
    setLoading,
    addTypingUser,
    removeTypingUser,
    setReplyingTo,
    updateConversationLastMessage,
    incrementConversationUnread,
    resetConversationUnread,
    clearAll,
} = internalChatSlice.actions

export default internalChatSlice.reducer
